import { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment-timezone";
import { format, isAfter, startOfToday } from "date-fns";
import DOMPurify from "dompurify";

const MyCalendar = () => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(
          "https://core.service.elfsight.com/p/boot/?page=https%3A%2F%2Fcaa10c58ff4a4973b61dc7dfbfb07b3d.elf.site%2F&w=caa10c58-ff4a-4973-b61d-c7dfbfb07b3d"
        );
        const eventsData = response.data.data.widgets?.["caa10c58-ff4a-4973-b61d-c7dfbfb07b3d"]?.data?.settings?.events;
        const fetchedEventTypes = response.data.data.widgets?.["caa10c58-ff4a-4973-b61d-c7dfbfb07b3d"]?.data?.settings?.eventTypes;
        const locationsData = response.data.data.widgets?.["caa10c58-ff4a-4973-b61d-c7dfbfb07b3d"]?.data?.settings?.locations || [];

        if (!eventsData || !fetchedEventTypes) {
          throw new Error("No events data or event types found");
        }

        const localTimezone = moment.tz.guess();

        const formattedEvents = eventsData.map((event) => {
          const eventStart = moment.tz(`${event.start.date}T${event.start.time}`, event.timeZone);
          const eventEnd = moment.tz(`${event.end.date}T${event.end.time}`, event.timeZone);
          const eventType = fetchedEventTypes.find((type) => type.id === event.eventType) || {};
          const eventLocation = locationsData.find((location) => location.id === event.location);
          const isMultiDay = !eventStart.isSame(eventEnd, "day");

          const backgroundColors = {
            "Live Stream": "#568f7a",
            "X Space": "#a551cf",
            "Community Party": "#e6a0e1",
            Gameplay: "#61cfcc",
            "PIXELS UPDATE": "#61cfcc",
            Carnival: "#61cfcc",
            default: "#e59e30",
          };

          return {
            id: event.id,
            title: event.name,
            start: eventStart.clone().tz(localTimezone).toDate(),
            end: eventEnd.clone().tz(localTimezone).toDate(),
            isMultiDay, // Add this flag
            endDateFormatted: isMultiDay ? eventEnd.clone().tz(localTimezone).format("MMMM D") : null,
            description: event.description,
            location: eventLocation ? eventLocation.name : event.location,
            locationObject: eventLocation,
            backgroundColor: backgroundColors[eventType.name] || backgroundColors["default"],
            borderColor: backgroundColors[eventType.name] || backgroundColors["default"],
            textColor: "#ffffff",
            type: eventType.name || "Event",
          };
        });

        const today = startOfToday();
        const futureEvents = formattedEvents.filter((event) => isAfter(event.start, today) || isAfter(event.end, today));

        // Sort events by start time
        futureEvents.sort((a, b) => b.start - a.start);

        setEvents(futureEvents);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching events:", error);
        setError(error.message);
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Group events by day
  const eventsByDay = events.reduce((acc, event) => {
    const dateKey = format(event.start, "yyyy-MM-dd");
    if (!acc[dateKey]) {
      acc[dateKey] = {
        date: event.start,
        events: [],
      };
    }
    acc[dateKey].events.push(event);
    return acc;
  }, {});

  // Convert to array and sort by date
  const sortedDayGroups = Object.values(eventsByDay).sort((a, b) => a.date - b.date);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded text-red-600">
        <p>Error loading events: {error}</p>
        <button onClick={() => window.location.reload()} className="mt-2 px-4 py-2 bg-red-100 hover:bg-red-200 rounded">
          Retry
        </button>
      </div>
    );
  }

  if (sortedDayGroups.length === 0) {
    return <div className="p-8 text-center text-[var(--text2-color)]">No upcoming events scheduled</div>;
  }

  return (
    <div className="space-y-4 md:space-y-6 panel-style">
      <h2 className="panel-heading">Pixels Event Calendar</h2>
      
      {sortedDayGroups.map((dayGroup, index) => (
        <div key={index} className="bg-[var(--entity-bg-color)] rounded-lg md:rounded-xl shadow-sm overflow-hidden">
          {/* Day Header - More compact on mobile */}
          <div className="bg-[var(--entity-mini)] px-4 py-2 md:px-6 md:py-3 border-b">
            <h2 className="text-base md:text-lg font-semibold text-[var(--text-color)]">
              {format(dayGroup.date, "EEEE, MMMM d")}
            </h2>
          </div>
  
          {/* Events List */}
          <div className="divide-y divide-gray-100">
            {dayGroup.events.map((event) => (
              <div 
                key={event.id} 
                className="px-4 py-3 md:px-6 md:py-4 hover:bg-[var(--hover-panel-color)] transition-colors duration-150"
              >
                <div className="flex flex-col md:flex-row md:items-start">
                  {/* Time Indicator - Stacked on mobile */}
                  <div className="mb-2 md:mb-0 md:mr-4 md:min-w-[100px]">
                    <div className="flex items-center md:block">
                      <span className="text-sm font-medium text-[var(--text3-color)] md:block">
                        {format(event.start, "h:mm a")}
                      </span>
                      <span className="mx-2 md:hidden">-</span>
                      <span className="text-xs text-[var(--text5-color)] md:block">
                        {format(event.end, "h:mm a")}
                        {event.isMultiDay && (
                          <span className="hidden md:block">{format(event.end, "MMM d")}</span>
                        )}
                      </span>
                    </div>
                    {event.isMultiDay && (
                      <div className="md:hidden text-xs mt-1 text-[var(--text5-color)]">
                        Ends {format(event.end, "MMM d")}
                      </div>
                    )}
                  </div>
  
                  {/* Event Details - Full width on mobile */}
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center mb-1">
                      {/* Event type badge */}
                      <span
                        className="inline-block px-2 py-0.5 text-xs font-medium rounded-full mb-1 sm:mb-0 sm:mr-2 w-fit"
                        style={{
                          backgroundColor: `${event.backgroundColor}20`,
                          color: event.backgroundColor,
                          border: `1px solid ${event.borderColor}`,
                        }}
                      >
                        {event.type}
                      </span>
                      <h3 className="text-base md:text-lg font-semibold text-[var(--text5-color)]">
                        {event.title}
                      </h3>
                    </div>
  
                    {event.description && (
                      <div 
                        className="text-sm text-[var(--text3-color)] mb-2"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(event.description) }} 
                      />
                    )}
  
                    {event.location && (
                      <div className="flex items-center text-xs md:text-sm font-semibold text-[var(--text-color)]">
                        <svg className="w-3 h-3 md:w-4 md:h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {event.location}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default MyCalendar;
